import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail } from "lucide-react";
import { DateTime } from "luxon";
import bgImage from "../../assets/images/launch/bg.jpg";
import { useAuth } from "@/context/Auth";
import { toast } from "sonner";

export default function Launch() {
  const targetDate = DateTime.fromObject(
    { year: 2025, month: 11, day: 10, hour: 0, minute: 0 },
    { zone: "Africa/Lagos" }
  ).toJSDate();

  const calculateTimeLeft = () => {
    const difference = +targetDate - +new Date();
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { subscribeToNewsletter } = useAuth();

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error("Email is required.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    try {
      await subscribeToNewsletter(email);
      setIsSubscribed(true);
      setEmail("");
    } catch {
      // Error toast already handled in the context
    }
  };

  const isLaunched = Object.values(timeLeft).every((val) => val === 0);

  return (
    <div
      className="relative bg-contain md:bg-cover bg-center min-h-screen flex items-center justify-center px-4 py-10"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="absolute inset-0 bg-white/20 backdrop-blur-sm" />
      <div className="relative z-10 max-w-2xl mx-auto text-center space-y-12">
        {/* Branding Intro */}
        <div className="space-y-4">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            Something Beautiful is Coming
          </h1>
          <p className="text-lg text-gray-600 max-w-md mx-auto">
            We’re building a skincare experience that will elevate your everyday
            routine.
          </p>
        </div>

        {/* Countdown Timer */}
        {!isLaunched && (
          <div className="space-y-6 px-4 sm:px-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xs sm:max-w-lg mx-auto">
              {Object.entries(timeLeft).map(([label, value]) => (
                <div key={label} className="text-center">
                  <div className="bg-white border rounded-lg p-4 shadow-sm">
                    <AnimatePresence mode="popLayout">
                      <motion.div
                        key={value}
                        initial={{ rotateX: 90, opacity: 0 }}
                        animate={{ rotateX: 0, opacity: 1 }}
                        exit={{ rotateX: -90, opacity: 0 }}
                        transition={{
                          duration: 0.5,
                          ease: [0.4, 0.0, 0.2, 1], // smooth easing curve
                        }}
                        className="text-3xl font-bold text-gray-900 mb-1 transform-gpu"
                      >
                        {value.toString().padStart(2, "0")}
                      </motion.div>
                    </AnimatePresence>
                    <div className="text-xs uppercase text-gray-500 font-medium tracking-wide">
                      {label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500">
              We’re launching on{" "}
              <span className="font-medium text-gray-900">
                October 10, 2025
              </span>
            </p>
          </div>
        )}

        {/* Subscription Form */}
        {!isLaunched && (
          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-gray-900">
                Stay in the loop
              </h3>
              <p className="text-gray-600">
                Sign up for early access and launch updates.
              </p>
            </div>

            {!isSubscribed ? (
              <form onSubmit={handleSubscribe} className="max-w-sm mx-auto">
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="submit"
                    className="bg-black hover:bg-gray-800 text-white px-6"
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-sm mx-auto">
                <p className="text-green-800 text-sm font-medium">
                  ✓ Thank you for subscribing! We'll keep you updated.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
